import { Check, Clock, Flag, StickyNote, TriangleAlert, type LucideIcon } from 'lucide-react'
import type { TickerKind } from './api'

/**
 * The ticker's colour per kind, in one place, for both themes.
 *
 * The light values are the prototype's (docs/ticker/ticker-prototype.html). The prototype has no
 * dark theme, so the dark values keep each hue and invert the roles: the chip becomes a tinted
 * translucent fill with a light foreground, which keeps the text readable on the dark surface
 * where the light theme's pale chip would glare.
 *
 * Every class is written out in full so Tailwind's scanner finds it (src/**\/*.{ts,tsx}).
 */
export interface KindPalette {
    /** The chip and the avatar: background and text. */
    chip: string
    /** The card's faint wash over the surface. */
    fill: string
    /** The card's hairline border. */
    border: string
    /** The 1 px countdown bar. */
    bar: string
    /** The chip's glyph, as the prototype: a tick, a warning triangle, a note, a flag; a clock for RTA. */
    icon: LucideIcon
}

export const KIND_PALETTE: Record<TickerKind, KindPalette> = {
    task_complete: {
        chip: 'bg-[#E1F5EE] text-[#0F6E56] dark:bg-[rgba(29,158,117,.20)] dark:text-[#6FD4AE]',
        fill: 'bg-[rgba(29,158,117,.06)] dark:bg-[rgba(29,158,117,.12)]',
        border: 'border-[rgba(29,158,117,.22)] dark:border-[rgba(29,158,117,.40)]',
        bar: 'bg-[#0F6E56] dark:bg-[#6FD4AE]',
        icon: Check,
    },
    object_complete: {
        chip: 'bg-[#E6F1FB] text-[#185FA5] dark:bg-[rgba(55,138,221,.22)] dark:text-[#8DBDF0]',
        fill: 'bg-[rgba(24,95,165,.06)] dark:bg-[rgba(55,138,221,.12)]',
        border: 'border-[rgba(24,95,165,.22)] dark:border-[rgba(55,138,221,.42)]',
        bar: 'bg-[#185FA5] dark:bg-[#8DBDF0]',
        icon: Check,
    },
    task_issue: {
        chip: 'bg-[#FCEBEB] text-[#A32D2D] dark:bg-[rgba(226,75,74,.22)] dark:text-[#F4A3A2]',
        fill: 'bg-[rgba(226,75,74,.07)] dark:bg-[rgba(226,75,74,.13)]',
        border: 'border-[rgba(226,75,74,.26)] dark:border-[rgba(226,75,74,.45)]',
        bar: 'bg-[#A32D2D] dark:bg-[#F4A3A2]',
        icon: TriangleAlert,
    },
    engineer_update: {
        chip: 'bg-[#EEEDFE] text-[#534AB7] dark:bg-[rgba(127,119,221,.24)] dark:text-[#BDB7F4]',
        fill: 'bg-[rgba(127,119,221,.07)] dark:bg-[rgba(127,119,221,.13)]',
        border: 'border-[rgba(127,119,221,.24)] dark:border-[rgba(127,119,221,.45)]',
        bar: 'bg-[#534AB7] dark:bg-[#BDB7F4]',
        icon: StickyNote,
    },
    management_update: {
        chip: 'bg-[#FAEEDA] text-[#854F0B] dark:bg-[rgba(239,159,39,.22)] dark:text-[#F5C377]',
        fill: 'bg-[rgba(239,159,39,.08)] dark:bg-[rgba(239,159,39,.12)]',
        border: 'border-[rgba(239,159,39,.26)] dark:border-[rgba(239,159,39,.45)]',
        bar: 'bg-[#854F0B] dark:bg-[#F5C377]',
        icon: Flag,
    },
    rta_change: {
        chip: 'bg-[#F0EAFB] text-[#6A3FB0] dark:bg-[rgba(124,77,221,.26)] dark:text-[#CBB2F5]',
        fill: 'bg-[rgba(124,77,221,.07)] dark:bg-[rgba(124,77,221,.14)]',
        border: 'border-[rgba(124,77,221,.26)] dark:border-[rgba(124,77,221,.48)]',
        bar: 'bg-[#6A3FB0] dark:bg-[#CBB2F5]',
        icon: Clock,
    },
}

/** A good or bad value in an expanded row (RTA met, an open issue, a slip). */
export const TONE_CLASS = {
    good: 'text-[#0F6E56] dark:text-[#6FD4AE]',
    bad: 'text-[#A32D2D] dark:text-[#F4A3A2]',
} as const

/** The search highlight, as the prototype's <mark>, dimmed for the dark theme. */
export const MARK_CLASS = 'rounded-[2px] bg-[#FFF1B8] px-px text-inherit dark:bg-[rgba(250,204,21,.28)]'

/** The row a clicked banner card opens on. */
export const HIGHLIGHT_ROW_CLASS = 'bg-[#fbf7ec] border-[rgba(239,159,39,.4)] dark:bg-[rgba(239,159,39,.10)] dark:border-[rgba(239,159,39,.45)]'
